import { type FormEvent, useEffect, useId, useState } from 'react'
import {
    KIND_LABEL,
    type ModelInput,
    type ModelKind,
    type ModelRow,
    PROVIDER_TYPE_LABEL,
    type ProviderType,
    testConnection,
} from '~/api/models'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Select } from '~/components/ui/select'
import { Spinner } from '~/components/ui/spinner'

// 모델 등록/편집 공용 모달. mode로 제목·버튼만 바꾸고, initial이 있으면 그 값으로 폼을 채운다.
// 실제 호출/토스트/새로고침은 상위(ModelsPage)가 onSubmit으로 처리한다(BulkEditDialog 패턴).
// 키는 실제 값을 입력받아 서버가 암호화 저장한다(다시 표시되지 않음). 비우면 기존 키를 유지한다.

interface FormState {
    name: string
    provider: string
    providerType: ProviderType
    modelId: string
    kind: ModelKind
    baseUrl: string
    apiKey: string
}

const EMPTY: FormState = {
    name: '',
    provider: 'openai',
    providerType: 'managed',
    modelId: '',
    kind: 'chat',
    baseUrl: '',
    apiKey: '',
}

function toState(model?: ModelRow | null): FormState {
    // 키는 절대 내려오지 않으므로 apiKey는 항상 빈 값으로 시작한다(비우면 기존 유지).
    if (!model) return EMPTY
    return {
        name: model.name,
        provider: model.provider,
        providerType: model.providerType,
        modelId: model.modelId,
        kind: model.kind,
        baseUrl: model.baseUrl ?? '',
        apiKey: '',
    }
}

export function ModelFormDialog({
    mode,
    initial,
    busy,
    onSubmit,
    onClose,
}: {
    mode: 'create' | 'edit'
    /** 편집 모드일 때 초기값. */
    initial?: ModelRow | null
    busy: boolean
    onSubmit: (input: ModelInput) => void
    onClose: () => void
}) {
    const id = useId()
    const [form, setForm] = useState<FormState>(() => toState(initial))
    // 저장 전 연결 점검 상태(입력한 원본 키로 핑). 결과는 폼 안에 인라인으로 보여준다.
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState<{
        ok: boolean
        text: string
    } | null>(null)

    // 다른 행을 편집하려 열면 그 행 값으로 다시 채운다.
    useEffect(() => {
        setForm(toState(initial))
        setTestResult(null)
    }, [initial])

    // 열려 있는 동안 Esc로 닫는다.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onClose])

    const submit = (e: FormEvent) => {
        e.preventDefault()
        onSubmit({
            name: form.name.trim(),
            provider: form.provider.trim(),
            providerType: form.providerType,
            modelId: form.modelId.trim(),
            kind: form.kind,
            baseUrl: form.baseUrl.trim() === '' ? null : form.baseUrl.trim(),
            apiKey: form.apiKey.trim() === '' ? null : form.apiKey.trim(),
        })
    }

    // 저장 전 연결 점검: 입력한 원본 키로 /models/test-connection에 핑(키는 저장하지 않음).
    const runPreSaveTest = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            const res = await testConnection({
                provider: form.provider.trim(),
                modelId: form.modelId.trim(),
                kind: form.kind,
                baseUrl:
                    form.baseUrl.trim() === '' ? null : form.baseUrl.trim(),
                apiKey: form.apiKey.trim() === '' ? null : form.apiKey.trim(),
            })
            setTestResult(
                res.ok
                    ? { ok: true, text: `연결 성공 (${res.latencyMs}ms)` }
                    : {
                          ok: false,
                          text: `연결 실패: ${res.error ?? '알 수 없는 오류'}`,
                      }
            )
        } catch (err) {
            setTestResult({ ok: false, text: (err as Error).message })
        } finally {
            setTesting(false)
        }
    }

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((f) => ({ ...f, [key]: value }))

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
            onClick={onClose}>
            <Card
                role='dialog'
                aria-modal='true'
                aria-labelledby={`${id}-title`}
                className='w-full max-w-xl p-5 shadow-sm'
                onClick={(e) => e.stopPropagation()}>
                <h2
                    id={`${id}-title`}
                    className='text-foreground text-base font-semibold tracking-tight'>
                    {mode === 'create' ? '모델 등록' : '모델 편집'}
                </h2>
                <p className='text-muted-foreground mt-1 text-[13px]'>
                    키는 암호화 저장되며 다시 표시되지 않아요. 변경하려면 새
                    키를 입력하고, 비우면 기존 키를 유지해요.
                </p>

                <form onSubmit={submit} className='mt-4'>
                    <div className='grid grid-cols-2 gap-4'>
                        <Field
                            label='레지스트리 이름'
                            value={form.name}
                            onChange={(v) => set('name', v)}
                            required
                        />
                        <Field
                            label='제공자 (provider)'
                            value={form.provider}
                            onChange={(v) => set('provider', v)}
                            required
                        />
                        <SelectField
                            label='제공 유형'
                            value={form.providerType}
                            onChange={(v) =>
                                set('providerType', v as ProviderType)
                            }
                            options={[
                                {
                                    value: 'managed',
                                    label: PROVIDER_TYPE_LABEL.managed,
                                },
                                {
                                    value: 'self_hosted',
                                    label: PROVIDER_TYPE_LABEL.self_hosted,
                                },
                            ]}
                        />
                        <SelectField
                            label='용도'
                            value={form.kind}
                            onChange={(v) => set('kind', v as ModelKind)}
                            options={[
                                { value: 'chat', label: KIND_LABEL.chat },
                                {
                                    value: 'embedding',
                                    label: KIND_LABEL.embedding,
                                },
                                { value: 'vision', label: KIND_LABEL.vision },
                            ]}
                        />
                        <Field
                            label='모델 ID (예: gpt-4o-mini)'
                            value={form.modelId}
                            onChange={(v) => set('modelId', v)}
                            required
                        />
                        <Field
                            label='Base URL (자가호스팅/게이트웨이, 선택)'
                            value={form.baseUrl}
                            onChange={(v) => set('baseUrl', v)}
                        />
                        <Field
                            label='API Key (실제 키 — 비우면 기존 유지)'
                            value={form.apiKey}
                            onChange={(v) => set('apiKey', v)}
                            type='password'
                            placeholder={
                                mode === 'edit'
                                    ? '변경하려면 새 키 입력'
                                    : 'sk-…'
                            }
                        />
                    </div>

                    <div className='mt-5 flex items-center justify-between gap-2'>
                        <div className='flex items-center gap-2'>
                            <Button
                                type='button'
                                variant='outline'
                                size='sm'
                                onClick={runPreSaveTest}
                                disabled={testing || form.apiKey.trim() === ''}
                                title='입력한 키로 저장 전에 연결을 점검해요(키는 저장하지 않아요).'>
                                {testing ? (
                                    <Spinner size='sm' />
                                ) : (
                                    '연결 테스트'
                                )}
                            </Button>
                            {testResult ? (
                                <Badge
                                    tone={testResult.ok ? 'success' : 'danger'}>
                                    {testResult.text}
                                </Badge>
                            ) : null}
                        </div>
                        <div className='flex gap-2'>
                            <Button
                                type='button'
                                variant='outline'
                                size='sm'
                                onClick={onClose}>
                                취소
                            </Button>
                            <Button type='submit' size='sm' disabled={busy}>
                                {busy
                                    ? '저장하는 중…'
                                    : mode === 'create'
                                      ? '모델 등록'
                                      : '변경 저장'}
                            </Button>
                        </div>
                    </div>
                </form>
            </Card>
        </div>
    )
}

function Field({
    label,
    value,
    onChange,
    required = false,
    type = 'text',
    placeholder,
}: {
    label: string
    value: string
    onChange: (v: string) => void
    required?: boolean
    type?: string
    placeholder?: string
}) {
    const id = useId()
    return (
        <div className='flex flex-col gap-1.5'>
            <Label htmlFor={id}>{label}</Label>
            <Input
                id={id}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                autoComplete={type === 'password' ? 'new-password' : undefined}
            />
        </div>
    )
}

function SelectField({
    label,
    value,
    onChange,
    options,
}: {
    label: string
    value: string
    onChange: (v: string) => void
    options: { value: string; label: string }[]
}) {
    const id = useId()
    return (
        <div className='flex flex-col gap-1.5'>
            <Label htmlFor={id}>{label}</Label>
            <Select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </Select>
        </div>
    )
}
